import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onboardingAPI, demoAPI } from '../api';

/**
 * DemoSpotlight — admin-controlled cinematic spotlight tour.
 *
 * Steps are fetched from /api/onboarding/steps?context=<context>. Admin edits
 * via Admin Panel → Onboarding tab. The board itself stays the visual hero.
 *
 * Props:
 *   active            — boolean, whether to show the tour
 *   onClose           — callback when user finishes/skips
 *   previewSteps      — optional array of admin preview steps (bypasses API)
 *   forcePreview      — boolean, when true does NOT call dismiss-guidance
 *                       (used by admin "Preview onboarding")
 *   context           — 'demo' (default) | 'library' | 'global' | 'first_login'
 *   placement         — slug used for dismiss/replay (default 'demo_spotlight')
 */
const DEFAULT_PADDING = 14;
const CARD_WIDTH = 380;
const CARD_GAP = 28;

const DemoSpotlight = ({
  active,
  onClose,
  previewSteps,
  forcePreview = false,
  context = 'demo',
  placement = 'demo_spotlight',
  // When the host page already knows which board surface it sits on,
  // pass the type so steps scoped to the opposite type are filtered
  // server-side. Caller is responsible for picking 'client' or
  // 'inspiration' from `board.board_type`. Empty/null = no scoping.
  boardType = null,
}) => {
  const [steps, setSteps] = useState(previewSteps || null);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [closing, setClosing] = useState(false);
  const [lang, setLang] = useState('en');

  // Tracks the most recent `pre_open_selector` the tour clicked. When the
  // step changes (or the tour closes), we click the same opener again so
  // the toggle-style panel returns to its starting state — the user
  // never sees a half-collapsed UI mid-tour. Only tour-initiated opens
  // are tracked; if the user opened the drawer themselves before the
  // tour started, we leave their state untouched (we record the click
  // only when WE issued it).
  const lastOpenedRef = useRef('');

  // Localized UI chrome — kept tiny so it stays in sync with the user's
  // workspace language. Only EN/PL/DE supported (matches backend).
  const UI = {
    en: { skip: 'Skip introduction', next: 'Next', begin: 'Begin', preview: 'preview' },
    pl: { skip: 'Pomiń wprowadzenie',  next: 'Dalej', begin: 'Rozpocznij', preview: 'podgląd' },
    de: { skip: 'Einführung überspringen', next: 'Weiter', begin: 'Beginnen', preview: 'Vorschau' },
  };
  const ui = UI[lang] || UI.en;

  // Load steps from API (skip if preview steps provided)
  useEffect(() => {
    if (previewSteps) { setSteps(previewSteps); return; }
    if (!active) return;
    let cancelled = false;
    demoAPI.getOnboardingSteps(context, boardType)
      .then(res => {
        if (cancelled) return;
        const list = res.data?.steps || [];
        setSteps(list);
        if (res.data?.lang) setLang(res.data.lang);
        // Admin "Preview step" deep-link: ?preview_onboarding=1&step=<id>
        // jumps the tour to a specific step so admins can fine-tune visually.
        try {
          const p = new URLSearchParams(window.location.search);
          const wantId = p.get('step');
          if (wantId) {
            const idx = list.findIndex((s) => s.id === wantId);
            if (idx >= 0) setStepIdx(idx);
          }
        } catch (e) { /* non-fatal */ }
      })
      .catch(() => { if (!cancelled) setSteps([]); });
    return () => { cancelled = true; };
  }, [active, previewSteps, context]);

  // Compute the spotlight rectangle for the current step.
  //
  // Resolution rules (mirrors the backend `_serialize`):
  //   - If `match_mode === 'group'`, take the UNION bounding box of every
  //     element matched by the selector (this is what fixes the "Exports area"
  //     case: PDF + ZIP buttons get spotlit together instead of just PDF).
  //   - Otherwise, take the first matched element only (intentional exemplar
  //     spotlighting for reactions/labels/etc.).
  //   - Apply admin overrides: spotlight_padding, spotlight_offset_x/y,
  //     spotlight_width_override (px, optional).
  //
  // Scroll handling: when the target is off-screen we use INSTANT scroll
  // (behavior:'auto') so the post-scroll bounding rect is correct on the very
  // next paint. Smooth scrolling between steps caused stale rects — the ring
  // ended up where the union *was* at measure time, not where it *is* after
  // the page settled.
  const measure = useCallback(() => {
    if (!steps || !steps[stepIdx]) { setRect(null); return; }
    const step = steps[stepIdx];
    const sel = step.selector;
    if (!sel) { setRect(null); return; }

    // Pre-open hook — when the spotlight target lives inside a panel
    // that's collapsed by default (e.g. PDF / ZIP exports inside the
    // Settings drawer), click `pre_open_selector` first so the target
    // is in the DOM before we measure. Idempotent: if the target is
    // already visible we skip the click. We wait a short beat for the
    // panel mount to settle and re-run measure().
    const targetMissing = (() => {
      try {
        const found = document.querySelectorAll(sel);
        return Array.from(found).every(el => {
          const r = el.getBoundingClientRect();
          return r.width === 0 && r.height === 0;
        });
      } catch (e) { return true; }
    })();
    const preOpenSel = (step.pre_open_selector || '').trim();
    if (preOpenSel && targetMissing) {
      try {
        const opener = document.querySelector(preOpenSel);
        if (opener) {
          opener.click();
          // Remember which opener we toggled so a later step (or the
          // tour closing) can put the panel back where it was.
          lastOpenedRef.current = preOpenSel;
          // Give React one frame to mount the panel, then re-measure.
          const idx = stepIdx;
          // eslint-disable-next-line no-use-before-define
          setTimeout(() => { if (steps && steps[idx]) measure(); }, 80);
          return;
        }
      } catch (e) { /* non-fatal — fall through to standard measure */ }
    }

    let matches;
    try {
      matches = Array.from(document.querySelectorAll(sel)).filter((el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      });
    } catch (e) {
      // Malformed selector (admin custom selector) — degrade gracefully.
      setRect(null);
      return;
    }
    if (matches.length === 0) { setRect(null); return; }

    const useGroup = (step.match_mode || 'first') === 'group' && matches.length > 1;
    const targets = useGroup ? matches : [matches[0]];

    const unionRect = () => {
      let top = Infinity, left = Infinity, right = -Infinity, bottom = -Infinity;
      for (const el of targets) {
        const r = el.getBoundingClientRect();
        if (r.top < top) top = r.top;
        if (r.left < left) left = r.left;
        if (r.right > right) right = r.right;
        if (r.bottom > bottom) bottom = r.bottom;
      }
      return { top, left, right, bottom };
    };

    const u = unionRect();
    const inView = u.top > 80 && u.bottom < window.innerHeight - 60;
    if (!inView) {
      // Instant scroll — smooth scroll races with the next measure() call
      // and produces stale rects. The card transition still feels cinematic
      // because the spotlight ring itself has its own CSS transition.
      targets[0].scrollIntoView({ behavior: 'auto', block: 'center' });
      // One more frame for layout to settle, then re-measure.
      requestAnimationFrame(() => {
        const u2 = unionRect();
        applyOverrides(step, u2);
      });
      return;
    }
    applyOverrides(step, u);
    // Safety: re-measure after a beat in case any sibling step's lingering
    // animation shifted the layout. Cheap, idempotent.
    setTimeout(() => {
      if (!steps || !steps[stepIdx] || steps[stepIdx] !== step) return;
      const again = unionRect();
      // Only update if it materially changed (avoid spurious re-renders).
      if (Math.abs(again.top - u.top) > 1 || Math.abs(again.left - u.left) > 1) {
        applyOverrides(step, again);
      }
    }, 220);

    function applyOverrides(s, box) {
      const offX = Number(s.spotlight_offset_x || 0);
      const offY = Number(s.spotlight_offset_y || 0);
      let width = box.right - box.left;
      const height = box.bottom - box.top;
      let left = box.left;
      if (s.spotlight_width_override) {
        const wo = Number(s.spotlight_width_override);
        if (wo > 0) {
          const cx = box.left + width / 2;
          left = cx - wo / 2;
          width = wo;
        }
      }
      setRect({ top: box.top + offY, left: left + offX, width, height });
    }
  }, [steps, stepIdx]);

  // Auto-close any panel the tour itself opened, when the current step
  // no longer needs it. We compare the previously-opened selector to
  // the new step's `pre_open_selector`: if they differ (including the
  // common "new step has none" case), we click the previous opener to
  // toggle the panel closed. Idempotent — we always clear the ref
  // before the click so a missing opener never traps the tour.
  useEffect(() => {
    if (!active) return;
    if (!steps || !steps[stepIdx]) return;
    const nextPreOpen = (steps[stepIdx].pre_open_selector || '').trim();
    const opened = lastOpenedRef.current;
    if (opened && opened !== nextPreOpen) {
      try {
        const closer = document.querySelector(opened);
        if (closer) closer.click();
      } catch (e) { /* non-fatal */ }
      lastOpenedRef.current = '';
    }
  }, [active, steps, stepIdx]);

  // Re-measure on resize/scroll. We intentionally do NOT lock body scroll
  // so the user can keep exploring the board while the tour is active —
  // the scroll listener below keeps the ring glued to its target as the
  // page moves underneath.
  useEffect(() => {
    if (!active) return;
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [active, measure]);

  // When the tour itself ends (active flips off, component unmounts, or
  // the user dismisses), close any drawer we opened so the editor isn't
  // left mid-state.
  useEffect(() => {
    if (active) return;
    const opened = lastOpenedRef.current;
    if (!opened) return;
    try {
      const closer = document.querySelector(opened);
      if (closer) closer.click();
    } catch (e) { /* non-fatal */ }
    lastOpenedRef.current = '';
  }, [active]);

  const handleSkip = useCallback(async () => {
    setClosing(true);
    if (!forcePreview) {
      try { await onboardingAPI.dismissGuidance(placement); } catch (e) { /* non-fatal */ }
    }
    setTimeout(() => { onClose && onClose(); }, 320);
  }, [forcePreview, onClose, placement]);

  const handleNext = useCallback(() => {
    if (!steps || stepIdx >= steps.length - 1) { handleSkip(); return; }
    setStepIdx((i) => i + 1);
  }, [steps, stepIdx, handleSkip]);

  // ESC + arrow nav
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleSkip();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      else if (e.key === 'ArrowLeft' && stepIdx > 0) setStepIdx(stepIdx - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, stepIdx, handleNext, handleSkip]);

  // Body scroll lock REMOVED — it was blocking the user from scrolling
  // the demo board while the tour was active. The outer overlay below
  // uses pointer-events:none so wheel/touch events pass through to the
  // underlying page; only the card and the (rare) full-screen backdrop
  // re-enable interaction explicitly.

  if (!active) return null;
  if (!steps) return null; // still loading
  if (steps.length === 0) return null; // no steps configured

  const step = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;
  const padding = Number.isFinite(Number(step?.spotlight_padding))
    ? Number(step.spotlight_padding)
    : DEFAULT_PADDING;

  // Position the card. Admin can override with step.position; default is auto.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;

  let cardStyle = {};
  if (rect) {
    const pos = step.position || 'auto';
    const tryRight = () => ({
      top: Math.max(24, Math.min(vh - 280, rect.top + rect.height / 2 - 120)),
      left: rect.left + rect.width + CARD_GAP,
    });
    const tryLeft = () => ({
      top: Math.max(24, Math.min(vh - 280, rect.top + rect.height / 2 - 120)),
      left: rect.left - CARD_WIDTH - CARD_GAP,
    });
    const tryBelow = () => ({
      top: rect.top + rect.height + CARD_GAP,
      left: Math.max(24, Math.min(vw - CARD_WIDTH - 24, rect.left + rect.width / 2 - CARD_WIDTH / 2)),
    });
    const tryAbove = () => ({
      top: Math.max(24, rect.top - 240),
      left: Math.max(24, Math.min(vw - CARD_WIDTH - 24, rect.left + rect.width / 2 - CARD_WIDTH / 2)),
    });

    const rightSpace = vw - (rect.left + rect.width) - CARD_GAP;
    const leftSpace = rect.left - CARD_GAP;
    const belowSpace = vh - (rect.top + rect.height) - CARD_GAP;

    if (pos === 'right' && rightSpace >= CARD_WIDTH + 24) cardStyle = tryRight();
    else if (pos === 'left' && leftSpace >= CARD_WIDTH + 24) cardStyle = tryLeft();
    else if (pos === 'bottom' && belowSpace >= 220) cardStyle = tryBelow();
    else if (pos === 'top') cardStyle = tryAbove();
    else if (rightSpace >= CARD_WIDTH + 24) cardStyle = tryRight();
    else if (leftSpace >= CARD_WIDTH + 24) cardStyle = tryLeft();
    else if (belowSpace >= 220) cardStyle = tryBelow();
    else cardStyle = tryAbove();
  } else {
    cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <div
      data-testid="demo-spotlight"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        opacity: closing ? 0 : 1,
        transition: 'opacity 0.32s ease',
        // Let wheel / touch events pass through to the board underneath
        // so the user can keep scrolling. The card and the no-target
        // backdrop re-enable interaction explicitly below.
        pointerEvents: 'none',
      }}
    >
      {!rect && (
        <div
          aria-hidden="true"
          onClick={handleSkip}
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(20,18,15,0.42)',
            transition: 'opacity 0.32s ease',
            pointerEvents: 'auto',
          }}
        />
      )}

      {rect && (
        <div
          data-testid="demo-spotlight-ring"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            borderRadius: 6,
            pointerEvents: 'none',
            boxShadow:
              '0 0 0 9999px rgba(20,18,15,0.62), ' +
              '0 0 0 1px rgba(255,247,235,0.30), ' +
              '0 0 22px 4px rgba(255,247,235,0.14)',
            transition:
              'top 0.5s cubic-bezier(0.22, 0.61, 0.36, 1), ' +
              'left 0.5s cubic-bezier(0.22, 0.61, 0.36, 1), ' +
              'width 0.5s cubic-bezier(0.22, 0.61, 0.36, 1), ' +
              'height 0.5s cubic-bezier(0.22, 0.61, 0.36, 1)',
            animation: 'maestraSpotlightPulse 2.6s ease-in-out infinite',
          }}
        />
      )}

      {/* Card */}
      <div
        data-testid={`demo-spotlight-card-${stepIdx + 1}`}
        style={{
          position: 'absolute',
          width: CARD_WIDTH,
          maxWidth: 'calc(100vw - 48px)',
          background: '#FBF8F2',
          padding: '28px 30px 24px',
          boxShadow: '0 20px 50px rgba(20,18,15,0.18), 0 1px 0 rgba(255,255,255,0.5) inset',
          borderRadius: 2,
          transition: 'top 0.42s cubic-bezier(0.22, 0.61, 0.36, 1), left 0.42s cubic-bezier(0.22, 0.61, 0.36, 1)',
          // Card must remain interactive even though the outer overlay
          // is pointer-events:none (so the board can be scrolled).
          pointerEvents: 'auto',
          ...cardStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase',
            color: 'var(--kahla-text-light)', fontWeight: 300,
          }}>
            {String(stepIdx + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}
            {forcePreview && <span style={{ marginLeft: 12, color: '#7a3a3a', fontStyle: 'italic', textTransform: 'none', letterSpacing: '0.04em' }}>{ui.preview}</span>}
          </span>
          <button
            type="button"
            onClick={handleSkip}
            data-testid="demo-spotlight-close-btn"
            title="Close"
            style={{
              background: 'none', border: 'none', padding: 4,
              fontSize: 14, lineHeight: 1, cursor: 'pointer',
              color: 'var(--kahla-text-light)', opacity: 0.5, transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
          >×</button>
        </div>

        {step.screenshot_url && (
          <img
            src={step.screenshot_url}
            alt=""
            data-testid="demo-spotlight-screenshot"
            style={{ width: '100%', display: 'block', marginBottom: 16, border: '1px solid var(--kahla-border)' }}
          />
        )}

        <h3
          data-testid="demo-spotlight-title"
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 22, fontWeight: 400, lineHeight: 1.22,
            color: 'var(--kahla-charcoal)',
            margin: '0 0 12px',
            letterSpacing: '-0.005em',
          }}
        >
          {step.title}
        </h3>

        <p
          data-testid="demo-spotlight-body"
          style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 16, lineHeight: 1.7,
            color: 'var(--kahla-text-light)',
            margin: '0 0 14px',
          }}
        >
          {step.body}
        </p>

        {step.helper && (
          <p
            data-testid="demo-spotlight-helper"
            style={{
              fontSize: 12, color: 'var(--kahla-text-light)',
              margin: '0 0 18px', lineHeight: 1.6, opacity: 0.85,
            }}
          >
            {step.helper}
          </p>
        )}

        <div style={{ display: 'flex', gap: 6, marginBottom: 22 }} aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              style={{
                width: 18, height: 1.5,
                background: i <= stepIdx ? 'var(--kahla-charcoal)' : 'rgba(166,152,136,0.35)',
                transition: 'background 0.3s',
                display: 'inline-block',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 18, justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={handleSkip}
            data-testid="demo-spotlight-skip-btn"
            style={{
              background: 'none', border: 'none', padding: '4px 0',
              fontFamily: 'inherit',
              fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              textDecoration: 'underline', textUnderlineOffset: 4,
              cursor: 'pointer',
            }}
          >
            {ui.skip}
          </button>
          <button
            type="button"
            onClick={handleNext}
            data-testid="demo-spotlight-next-btn"
            style={{
              background: 'var(--kahla-charcoal)', color: '#fff',
              border: 'none', padding: '10px 22px',
              fontFamily: 'inherit',
              fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
              cursor: 'pointer', transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.85)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}
          >
            {isLast ? ui.begin : ui.next}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoSpotlight;
